<template>
    <BaseDialog
        :showDialog="showDialog"
        maxDialogWidth="1000"
        maxCardHeight="700"
        headline="lang_GENERAL_FILE_IMPORT_DIALOG_TITLE_UPLOAD_FILE"
        :close="onClose"
        persistent
        :alvqa="alvqaContext + '-' + alvqa"
    >
        <template v-slot:headerActions>
            <span v-if="domainConfig.contextHelp" class="mt-3">
                <ContextHelpIcon
                    :context="domainConfig.contextHelp"
                    :alvqa="alvqaContext + '-' + alvqa + '-context-help-icon'"
                />
            </span>
        </template>

<!-- Dialog Text -->
        <template v-slot:dialogText>
            <v-container fluid>
                <v-row v-if="dialogText" class="mb-3">
                    <v-col
                        v-html="dialogText"
                        :alvqa="alvqaContext + '-' + alvqa + '-text'"
                    ></v-col>
                </v-row>

<!-- Standalone report download (error / legacy-finished phases) -->
                <v-row v-if="reportPath && !showSummary">
                    <v-col class="d-flex align-center">
                        {{ $t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT') }}
                        <BaseIcon
                            @click.stop="downloadFile(reportPath)"
                            icon="cloud_download"
                            :tooltipText="$t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT')"
                            :alvqa="alvqaContext + '-' + alvqa + '-icon-download-report'"
                        />
                    </v-col>
                </v-row>

<!-- Domain-specific option fields (mapping dropdown, tags, checkboxes, ...) -->
                <slot v-if="showUploadOptions"></slot>

<!-- Progressbars -->
                <v-row v-if="showUploadProgressbar">
                    <v-col>
                        <v-progress-linear
                            v-model="uploadProgressbarValue"
                            height="25"
                            color="dialogProgressbar"
                            :alvqa="alvqaContext + '-' + alvqa + '-upload-progressbar'"
                        ></v-progress-linear>
                    </v-col>
                </v-row>
                <v-row v-if="showProcessProgressbar">
                    <v-col>
                        <v-progress-linear
                            v-model="processValue"
                            height="25"
                            color="dialogProgressbar"
                            :alvqa="alvqaContext + '-' + alvqa + '-process-progressbar'"
                        >{{ processValue }} %</v-progress-linear>
                    </v-col>
                </v-row>

<!-- Preflight summary (completeMsgArray.awaitingConfirmation === true) -->
                <div v-if="showSummary && summary" :alvqa="alvqaContext + '-' + alvqa + '-summary'">
                    <v-row class="mb-1">
                        <v-col class="font-weight-bold">
                            {{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_PREFLIGHT_SUMMARY') }}
                        </v-col>
                    </v-row>

<!-- The two figures the user actually decides on -->
                    <v-row dense>
                        <v-col cols="8">{{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_TO_CREATE') }}</v-col>
                        <v-col cols="4" class="text-right font-weight-bold" :alvqa="alvqaContext + '-' + alvqa + '-summary-to-create'">
                            {{ summary.toCreate }}
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="8">{{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_TO_UPDATE') }}</v-col>
                        <v-col cols="4" class="text-right font-weight-bold" :alvqa="alvqaContext + '-' + alvqa + '-summary-to-update'">
                            {{ toUpdateTotal }}
                        </v-col>
                    </v-row>

<!-- Secondary detail: why an incoming row was taken for an existing entity.
     Labels come from the BE, every method is always present (incl. zeros). -->
                    <template v-if="updateMethods.length">
                        <v-row dense class="mt-1">
                            <v-col>
                                {{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_MATCHED_BY') }}
                            </v-col>
                        </v-row>
                        <v-row
                            dense
                            align="center"
                            v-for="method in updateMethods"
                            :key="method.key"
                            :class="{ 'text--disabled': method.count === 0 }"
                        >
                            <v-col cols="8" class="d-flex align-center pl-5">
                                {{ method.label }}
                                <BaseIcon
                                    v-if="method.hint"
                                    icon="info_outline"
                                    small
                                    :tooltipText="method.hint"
                                    :alvqa="alvqaContext + '-' + alvqa + '-summary-method-' + method.key + '-hint'"
                                />
                            </v-col>
                            <v-col cols="4" class="text-right" :alvqa="alvqaContext + '-' + alvqa + '-summary-method-' + method.key">
                                {{ method.count }}
                            </v-col>
                        </v-row>
                    </template>

                    <v-divider class="mt-2 mb-2"></v-divider>

<!-- Rows the parser saw in the file. Comes from a different pass than
     toCreate/toUpdate, so it is deliberately NOT presented as their sum. -->
                    <v-row dense>
                        <v-col cols="8">
                            {{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_TOTAL_DETECTED') }}
                        </v-col>
                        <v-col cols="4" class="text-right" :alvqa="alvqaContext + '-' + alvqa + '-summary-total-detected'">
                            {{ summary.totalDetected }}
                        </v-col>
                    </v-row>

<!-- Validation counts (only when non-zero). Warnings do NOT block the import. -->
                    <v-row dense v-if="validationErrorCount > 0" class="mt-2">
                        <v-col :alvqa="alvqaContext + '-' + alvqa + '-summary-validation-errors'">
                            {{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_VALIDATION_ERRORS') }}: {{ validationErrorCount }}
                        </v-col>
                    </v-row>
                    <v-row dense v-if="validationWarningCount > 0">
                        <v-col :alvqa="alvqaContext + '-' + alvqa + '-summary-validation-warnings'">
                            {{ $t('lang_GENERIC_MAPPING_IMPORT_CAPTION_VALIDATION_WARNINGS') }}: {{ validationWarningCount }}
                        </v-col>
                    </v-row>

<!-- Detailed per-row report of the dry-run -->
                    <v-row v-if="reportPath" class="mt-1">
                        <v-col class="d-flex align-center">
                            {{ $t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT') }}
                            <BaseIcon
                                @click.stop="downloadFile(reportPath)"
                                icon="cloud_download"
                                :tooltipText="$t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT')"
                                :alvqa="alvqaContext + '-' + alvqa + '-summary-icon-download-report'"
                            />
                        </v-col>
                    </v-row>
                </div>
            </v-container>
        </template>

<!-- Actions -->
        <template v-slot:actionsWithValidation>
<!-- Select phase: choose file + upload (triggers the preflight when supported) -->
            <v-card-actions v-if="showUploadActions" class="pt-0 pb-0 pr-6 pl-6">
                <v-container fluid>
                    <v-form v-model="valid">
                        <v-row>
                            <v-col cols="8">
                                <v-file-input
                                    v-model="file"
                                    :label="$t('lang_GENERAL_CAPTION_SELECT_FILE')"
                                    :rules="rules"
                                    @change="fileExt = $alvPlugin.extractFileExt(file)"
                                    @click:clear="resetVal()"
                                    show-size
                                    :alvqa="alvqaContext + '-' + alvqa + '-file-input-import-file'"
                                ></v-file-input>
                            </v-col>
                            <v-spacer />
                            <v-col class="text-right" align-self="center">
                                <BaseButton
                                    :disabled="!valid || !file || !selectedMapping || busy"
                                    @click="submitFile"
                                    label="lang_GENERAL_CAPTION_UPLOAD"
                                    :alvqa="alvqaContext + '-' + alvqa + '-button-upload'"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-actions>

<!-- Confirmation phase: run the real import or throw the dry-run away -->
            <v-card-actions v-if="showSummary" class="pt-0 pb-4 pr-6 pl-6">
                <v-spacer />
                <BaseButton
                    color="buttonStandard"
                    label="lang_GENERAL_BUTTON_CANCEL"
                    :disabled="busy"
                    :loading="busy"
                    @click="abandonImport"
                    :alvqa="alvqaContext + '-' + alvqa + '-button-abandon'"
                />
                <BaseButton
                    color="buttonNewEntity"
                    label="lang_GENERIC_MAPPING_IMPORT_BUTTON_RUN_IMPORT"
                    :disabled="busy"
                    :loading="busy"
                    @click="confirmImport"
                    :alvqa="alvqaContext + '-' + alvqa + '-button-run-import'"
                />
            </v-card-actions>
        </template>
    </BaseDialog>
</template>

<script>
import api from '@/api_alv'
import BaseDialog from '../_base/BaseDialog'
import BaseButton from '../_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import ContextHelpIcon from '@/components/_buttons/ContextHelpIcon'

/** Tooltips for the "matched by" breakdown, keyed by the BE's byMethod key.
 *  Only methods a bare number would be misread for need one. */
const METHOD_HINTS = {
    // Users read this count as a bug otherwise: it means the same entity shows
    // up more than once in the sheet - the first row creates it, the later ones
    // update that same one.
    sameFile: 'lang_GENERIC_MAPPING_IMPORT_TOOLTIP_SAME_FILE'
}

/**
 * Reusable, domain-agnostic mapping-import dialog with a preflight (dry-run)
 * confirmation step. Everything domain-specific arrives through `domainConfig`
 * (see mappingImportConfigs.js). Visibility is driven by the SAME shared store
 * flag as FileImportDialog (getShowFileImportDialog), so the existing
 * ImportButton -> doFileImport open path works unchanged; the page chooses which
 * dialog to render via a v-if on the selected import option.
 *
 * Lifecycle:
 *   select -> PUT file with phase=preflight -> poll the returned processId
 *     -> completeMsgArray.awaitingConfirmation: summary -> Run import | Abandon
 *     -> commit: POST the SAME file_id, poll the NEW processId -> finished
 *   When domainConfig.supportsPreflight is false it falls back to today's
 *   single-shot behaviour (upload -> poll -> finished).
 *
 * Two things about the wire contract are easy to get wrong and are handled here
 * rather than by the BE:
 *  - a finished preflight reports status 'finished', exactly like a completed
 *    import (it has to: anything else counts against the "one import at a time"
 *    limit and would deadlock the commit). The hold is only visible through
 *    completeMsgArray.awaitingConfirmation - never through status.
 *  - the import options are not carried over from the preflight to the commit
 *    and the BE does not check that they match, so they are frozen at upload
 *    time and replayed verbatim (see preflightParams).
 */
export default {
    name: "MappingImportDialog",
    components: {
        BaseDialog,
        BaseButton,
        BaseIcon,
        ContextHelpIcon
    },
    props: {
        // Per-domain configuration (mappingImportConfigs.js entry).
        domainConfig: {
            type: Object,
            required: true
        },
        alvqaContext: {
            type: String,
            default: ''
        },
        // Optional override for the list-reload context; defaults to routeName.
        context: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            valid: false,
            formData: new FormData(),
            file: null,
            fileExt: '',
            showUploadProgressbar: false,
            showProcessProgressbar: false,
            showUploadOptions: true,
            showUploadActions: true,
            showSummary: false,
            summary: null,
            reportPath: null,
            fileId: null,
            // Options the preflight ran with, replayed verbatim on commit.
            preflightParams: null,
            activeProcessId: null,
            processValue: 0,
            polling: null,
            pollingTimeout: null,
            // null = fall back to the shared store text (e.g. the page's
            // "no mappings available" warning). A string takes over the display
            // for the dialog's own lifecycle messages.
            localDialogText: null,
            busy: false,
            rules: [value => !value || this.isFileExtAccepted() || this.$t('lang_GENERAL_DIALOG_TEXT_INVALID_FORMAT')],
            alvqa: 'mapping-import-dialog'
        }
    },
    computed: {
        showDialog: {
            get() { return this.$store.getters.getShowFileImportDialog },
            set(show) { this.$store.commit('setShowFileImportDialog', show) }
        },
        uploadProgressbarValue: {
            get() { return this.$store.getters.getUploadProgressbarValue },
            set(value) { this.$store.commit('setUploadProgressbarValue', value) }
        },
        dialogText() {
            return this.localDialogText !== null ? this.localDialogText : this.$store.getters.getImportDialogText
        },
        selectedMapping() {
            return this.$store.getters[this.domainConfig.mappingGetter]
        },
        tags() {
            if (!this.domainConfig.tagsGetter) return null
            return this.$store.getters[this.domainConfig.tagsGetter]
        },
        toUpdateTotal() {
            return this.summary && this.summary.toUpdate ? this.summary.toUpdate.total : 0
        },
        updateMethods() {
            if (!this.summary || !this.summary.toUpdate || !this.summary.toUpdate.byMethod) return []
            const byMethod = this.summary.toUpdate.byMethod
            return Object.keys(byMethod).map(key => ({
                key: key,
                label: this.$alvPlugin.isLangConst(byMethod[key].label)
                    ? this.$t(byMethod[key].label)
                    : byMethod[key].label,
                count: byMethod[key].count,
                hint: METHOD_HINTS[key] || null
            }))
        },
        validationErrorCount() {
            return this.summary && this.summary.validation ? (this.summary.validation.errorCount || 0) : 0
        },
        validationWarningCount() {
            return this.summary && this.summary.validation ? (this.summary.validation.warningCount || 0) : 0
        }
    },
    methods: {
        // --- Upload + preflight -------------------------------------------------

        async submitFile() {
            if (this.busy) return

            // Freeze the options the dry-run is about to run with. The commit
            // has to replay these exactly: the BE neither remembers them nor
            // checks them, so re-reading the store at commit time could quietly
            // import something other than what the user approved on screen.
            this.preflightParams = {
                type: this.$store.getters.getSelectedFileImport,
                mappingAlvId: this.selectedMapping,
                tag: this.tags,
                extra: this.domainConfig.extraParams ? this.domainConfig.extraParams(this.$store.getters) : {}
            }

            // Fresh FormData per attempt - reusing the instance would keep the
            // file appended by a previous, failed attempt.
            this.formData = new FormData()
            this.formData.append('file', this.file)

            this.localDialogText = this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_FILE_IS_BEING_UPLOADED')
            this.showUploadOptions = false
            this.showUploadActions = false
            this.showUploadProgressbar = true
            this.busy = true

            const uploadResponse = await this.uploadFile()
            this.showUploadProgressbar = false
            this.busy = false

            if (!uploadResponse || uploadResponse.error !== -1) {
                // Two error shapes come back from the upload: most carry
                // { response: { errorMsg } }, but the param/phase validation
                // branches put errorMsg at the top level instead.
                const errResponse = (uploadResponse && uploadResponse.response) || uploadResponse || {}
                this.showError({ msg: errResponse.errorMsg, path: errResponse.path })
                return
            }

            // Only a fileId that came out of THIS preflight may ever be
            // committed - the BE happily imports any file_id it is handed.
            this.fileId = uploadResponse.fileId !== null && uploadResponse.fileId !== undefined
                ? uploadResponse.fileId
                : null

            if (uploadResponse.processId === null || uploadResponse.processId === undefined) {
                // Nothing to poll: treat as a finished no-op.
                this.handleFinishedSuccess(uploadResponse)
                return
            }

            if (this.domainConfig.supportsPreflight && this.fileId === null) {
                // Without a fileId the dry-run could never be committed, so stop
                // here instead of showing a summary with a dead Import button.
                this.showError({ msg: 'lang_FILE_UPLOAD_ERROR' })
                return
            }

            this.localDialogText += '<br />' + this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_FILE_IS_BEING_PROCESSED')
            await this.startProcess(uploadResponse.processId)
        },

        /** Build the (preflight) upload params and PUT the file. Everything
         *  domain-specific comes from the frozen preflightParams / domainConfig. */
        async uploadFile() {
            const p = this.preflightParams
            let params = '?type=' + encodeURIComponent(p.type)
            params += '&mappingAlvId=' + encodeURIComponent(p.mappingAlvId)

            Object.keys(p.extra).forEach(key => {
                const value = p.extra[key]
                if (value !== null && value !== undefined) params += '&' + key + '=' + encodeURIComponent(value)
            })

            if (p.tag !== null && p.tag !== undefined && p.tag !== '') {
                params += '&tag=' + encodeURIComponent(p.tag)
            }

            if (this.domainConfig.supportsPreflight) params += '&phase=preflight'

            return await api.putFile(this.domainConfig.uploadDir, this.formData, params)
        },

        // --- Process polling ----------------------------------------------------

        async startProcess(processId) {
            this.activeProcessId = processId
            this.showProcessProgressbar = true
            const first = await this.pollTick()
            // Only keep polling if the first tick has not already reached a
            // terminal state - a small preflight can be done before we get here.
            if (first && first.status !== 'finished') {
                this.polling = setInterval(() => { this.pollTick() }, 5000)
                this.pollingTimeout = setTimeout(() => { this.clearPolling() }, 1800000)
            }
        },

        async pollTick() {
            let result = null
            try {
                result = await api.getProgressOfProcess(this.activeProcessId, this.polling)
            } catch (e) {
                // Transient failure of the progress endpoint: report "still
                // running" so the interval keeps ticking instead of leaving the
                // dialog stuck on a progressbar that never moves again.
                return { status: 'running' }
            }
            if (!result) return { status: 'running' }
            if (result.percent !== null && result.percent !== undefined) this.processValue = result.percent
            if (result.status !== 'finished') return result

            // 'finished' covers three different outcomes; only completeMsgArray
            // tells them apart (see the class comment).
            this.clearPolling()
            const completeMsg = result.completeMsgArray || {}

            if (result.error === 1) {
                this.showError({
                    // The progress endpoint builds top-level txt/errorMsg from
                    // process_message['data'], a key the works parser does not
                    // write - the real message is completeMsgArray.txt.
                    msg: result.errorMsg || completeMsg.errorMsg || completeMsg.txt,
                    path: result.path || completeMsg.path,
                    dialogCode: result.dialogCode,
                    dialogParameters: result.dialogParameters
                })
                return result
            }

            if (completeMsg.awaitingConfirmation === true) {
                this.showPreflightSummary(result, completeMsg)
                return result
            }

            this.handleFinishedSuccess(result)
            return result
        },

        /** Dry-run succeeded and nothing was written: hold for the user. */
        showPreflightSummary(result, completeMsg) {
            this.showProcessProgressbar = false
            this.summary = completeMsg.summary || null
            this.reportPath = completeMsg.path || result.path || (this.summary && this.summary.reportPath) || null
            this.localDialogText = this.$t('lang_GENERIC_MAPPING_IMPORT_TEXT_CONFIRM_PROMPT')
            this.showSummary = true
        },

        // --- Confirm / abandon --------------------------------------------------

        async confirmImport() {
            if (this.busy) return
            if (this.fileId === null || this.fileId === undefined) {
                this.showError({ msg: 'lang_FILE_UPLOAD_ERROR' })
                return
            }

            this.busy = true
            this.showSummary = false
            this.summary = null
            this.reportPath = null
            this.localDialogText = this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_FILE_IS_BEING_PROCESSED')

            const p = this.preflightParams || {}
            let response = null
            try {
                response = await api.confirmMappingImport({
                    dir: this.domainConfig.uploadDir,
                    fileId: this.fileId,
                    mappingAlvId: p.mappingAlvId,
                    tag: p.tag,
                    extra: p.extra
                })
            } catch (e) {
                response = null
            }
            this.busy = false

            if (response && response.error !== 1 && response.processId !== null && response.processId !== undefined) {
                // The file is consumed by the commit: drop the id so a later
                // close cannot abandon a file the import is working on.
                this.fileId = null
                await this.startProcess(response.processId)
            } else {
                const errResponse = (response && response.response) || response || {}
                this.showError({ msg: errResponse.errorMsg, path: errResponse.path })
            }
        },

        async abandonImport() {
            if (this.busy) return
            this.busy = true
            try {
                await this.discardUploadedFile()
            } finally {
                this.busy = false
                this.closeDialog()
            }
        },

        /** Best-effort cleanup of the temporary upload. Abandon is the only
         *  thing that removes it, so it runs on Cancel, on the dialog's X and
         *  after a failed preflight. Never surfaces its own errors. */
        discardUploadedFile() {
            if (!this.domainConfig.supportsPreflight) return Promise.resolve()
            if (this.fileId === null || this.fileId === undefined) return Promise.resolve()

            const fileId = this.fileId
            this.fileId = null
            return api
                .abandonMappingImport({ dir: this.domainConfig.uploadDir, fileId: fileId })
                .catch(() => {})
        },

        // --- Result handling ----------------------------------------------------

        handleFinishedSuccess(result) {
            this.showProcessProgressbar = false
            this.showSummary = false
            this.fileId = null
            this.localDialogText = ''

            // Optional BE message (e.g. the "finished with warnings" lang const).
            let txt = null
            if (result) {
                if (result.txt) txt = result.txt
                else if (result.completeMsgArray && result.completeMsgArray.txt) txt = result.completeMsgArray.txt
            }
            if (txt) {
                this.localDialogText += (this.$alvPlugin.isLangConst(txt) ? this.$t(txt) : txt) + '<br />'
            }

            this.localDialogText += this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_PROCESSING_SUCCESS') + '<br />' + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')

            if (result && result.path) {
                this.localDialogText += '<br /><br /><a href="' + result.path + '" target="_blank">' + this.$t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT') + '</a>'
            }

            this.reloadList()
        },

        showError(opts) {
            this.clearPolling()
            this.showUploadProgressbar = false
            this.showProcessProgressbar = false
            this.showSummary = false
            this.summary = null

            // A failed preflight can still have left the temporary upload behind.
            this.discardUploadedFile()

            if (opts.path) this.reportPath = opts.path

            if (opts.dialogCode) {
                this.localDialogText = this.$alvPlugin.buildDialogMessage(opts.dialogCode, opts.dialogParameters)
            } else {
                const msg = opts.msg || 'lang_FILE_UPLOAD_ERROR'
                this.localDialogText = (this.$alvPlugin.isLangConst(msg) ? this.$t(msg) : msg) + '<br /><br />' + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')
            }

            // Let the user correct the options and re-upload without having to
            // close and reopen the dialog.
            this.showUploadOptions = true
            this.showUploadActions = true

            this.reloadList()
        },

        reloadList() {
            const ctx = this.context !== '' ? this.context : this.domainConfig.routeName
            this.$store.dispatch('fetch' + ctx)
        },

        // --- File validation ----------------------------------------------------

        // Accepts the file only if its extension matches the selected mapping's
        // declared file format (with the usual csv/txt equivalence).
        isFileExtAccepted() {
            const mappings = this.$store.getters.getMappings ? this.$store.getters.getMappings : []
            const mappingObject = mappings.find(v => v.alvId == this.selectedMapping)
            if (mappingObject === undefined) return false
            const fmt = mappingObject.fileFormat.toLowerCase()
            return fmt === this.fileExt.toLowerCase() || (fmt === 'csv' && this.fileExt.toLowerCase() === 'txt')
        },

        // --- Misc ---------------------------------------------------------------

        async resetVal() {
            await this.$nextTick()
            this.file = null
        },

        downloadFile(path) {
            api.getFileFromALV(path)
        },

        clearPolling() {
            if (this.polling) {
                clearInterval(this.polling)
                this.polling = null
            }
            if (this.pollingTimeout) {
                clearTimeout(this.pollingTimeout)
                this.pollingTimeout = null
            }
        },

        // BaseDialog close (X). While awaiting confirmation this must discard the
        // temporary upload so the dry-run leaves nothing behind.
        onClose() {
            if (this.showSummary) this.abandonImport()
            else this.closeDialog()
        },

        closeDialog() {
            this.clearPolling()
            this.showDialog = false
            this.showUploadProgressbar = false
            this.showProcessProgressbar = false
            this.showUploadOptions = true
            this.showUploadActions = true
            this.showSummary = false
            this.summary = null
            this.reportPath = null
            this.fileId = null
            this.preflightParams = null
            this.activeProcessId = null
            this.file = null
            this.formData = new FormData()
            this.processValue = 0
            this.uploadProgressbarValue = 0
            this.localDialogText = null
            this.busy = false
            this.$store.commit('setSelectedFileImport', [])

            if (this.domainConfig.resetMutations) {
                Object.keys(this.domainConfig.resetMutations).forEach(mutation => {
                    this.$store.commit(mutation, this.domainConfig.resetMutations[mutation])
                })
            }
        }
    },
    beforeDestroy() {
        this.clearPolling()
        // Navigating away mid-summary would otherwise leave the upload behind.
        if (this.showSummary) this.discardUploadedFile()
    }
}
</script>
